import type { Controller } from '@data-client/react';

/**
 * DataClientController - I manage access to the Data Client `Controller` from non-hook code.
 * ONLY concerned with storing and retrieving the controller reference —
 * no React rendering, no network operations, no business logic.
 *
 * The controller is a React context value; it's populated by the
 * `DataClientProviderBridge` component mounted inside `<DataProvider>`
 * at the app root. Once mounted, any imperative caller can reach the
 * controller via `DataClientController.get()`.
 */
let controllerRef: Controller | null = null;

export const DataClientController = {
  set(controller: Controller | null) {
    controllerRef = controller;
  },
  get() {
    if (!controllerRef) {
      throw new Error(
        '@axiom/ui: Data Client controller is not registered. Ensure' +
          ' <DataProvider> and <DataClientProviderBridge /> are mounted at the' +
          ' app root before any Api.write / Api.invalidate call runs.'
      );
    }
    return controllerRef;
  },
  has() {
    return controllerRef !== null;
  },
  /**
   * Non-throwing identity check, so a render-phase registration can skip a
   * redundant write without `get()`'s "not registered" throw.
   */
  is(controller: Controller | null) {
    return controllerRef === controller;
  },
  /** Reset module state — for use in tests only. */
  reset() {
    controllerRef = null;
  },
};
